import i18n from "@/i18n";
import { sumFilter } from "fs-pb-vue2/filters";
import { BaseContractVueGB } from "./BaseContractVueGB";
import  dateFilter  from "fs-pb-vue2/filters/date.filter";
import IAlertService from "fs-pb-vue2/services/alert/IAlertService";
import { container } from "@/inversify.config";
import { DiTokens } from "fs-pb-vue2/services/diTokens";
import { ContractsApi } from "fs-pb-vue2/services/api/ContractsApi";

/** Модель представления счета */
export class CreditVueGB extends BaseContractVueGB {

  // сервис уведомлений
  get alertService(): IAlertService {
    return container.get<IAlertService>(DiTokens.AlertService);
  }

  async scheduleDialogModal(){
    this.scheduleDialog = true;
    if (!this.contract.CreditSchedule) {
      await this.contractsStore.fetchScheduleCredit(
        this.contract.Data.Id
      );
    }
  }

  get creditPaySum(): string {
    if (!this.contract.Data.DepositSum)
        return sumFilter(this.contract.Data.MinPaySum);

    const result = this.contract.Data.MinPaySum - this.contract.Data.DepositSum;
    return  sumFilter(result < 0 ? 0 : result);
  }

  get MinPayDate(): string {
    if (this.contract.Data.MinPayDate) 
        return dateFilter(this.contract.Data.MinPayDate, "date");
    return " ";
  }

  async callAction(action: () => Promise<void>) {
    await action();
  }
  
  async getContractByNum(num: string): Promise<number | null> {
    
    let contract: number | null = null;
  
    try {
      const contracts = await ContractsApi.getAllContractsAsync();
      const linkedContract = contracts.find((item: any) => item.MainAccount === num);
  
      if (linkedContract) {
        contract = Number(linkedContract.ReferenceId);
      }
    } catch (e: any) {
      this.alertService.error(e.message);
    }

    return contract;
  }

  get linkedAcntId(): Promise<number | string> {
    return this.contract && this.contract.Data.LinkedDepositAccountNum
      ? this.getContractByNum(this.contract.Data.LinkedDepositAccountNum)
      : Promise.resolve('');
  }

  async handleBtnClick() {
    const linkedAcntId = await this.linkedAcntId;
    
    this.$router.push(`/operation/0/${this.configStore.apiData.OperationCodeOfToContract}?contractTo=${linkedAcntId}&sum=${this.creditPaySum}`);
  }

  /**Список доступных операций */
  get actions(): Array<any> {
    const actions: Array<any> = [];
    if (this.contract) {
      /**Информация*/
      actions.push({
        text: i18n.t("main.contracts.actions.info"),
        iconClass: "information-action-icon",
        action: () => {
          this.$router.push({ path: this.$route.fullPath, query: { 'details': 'true' } });
        }
      });
      /**Действие запроса выписки по счету*/
      actions.push({
        text: i18n.t("main.contracts.actions.statement"),
        iconClass: "statement-icon",
        action: () => {
          this.$router.push(`/statement/${this.contract.Data.Id}/0`);
        }
      });
      /**Действие запроса реквизитов счета */
      actions.push({
        text: i18n.t("main.contracts.actions.requisits"),
        iconClass: "requisits-icon",
        action: () => {
          this.$router.push({ path: this.$route.fullPath, query: { 'reqs': 'true' } });
        }
      });
    }
    if (this.contract.IsOpen && this.configStore.apiData.OperationCodeOfEarlyRepayment) { 
      actions.push({
        text: this.$i18n.t("main.contracts.actions.earlyRepayment"),
        iconClass: "choose-card-icon",
        action: () => {
          this.$router.push(
            `/operation/0/${this.configStore.apiData.OperationCodeOfEarlyRepayment}?contract=${this.contract.Data.ReferenceId}&earlyRepayment=true`
          );
        }
      });
    }
    if (this.configStore.apiData.OperationCodeOfToContract && this.contract.IsOpen && this.contract.Data.CanWithdraw) {
      actions.push({
        text: this.$i18n.t("main.contracts.actions.closeDeposit"),
        iconClass: "close-deposit-icon",
        action: () => {
          this.$router.push(
            `/operation/0/${this.configStore.apiData.OperationCodeOfToContract}?_categoryName=${this.$i18n.t("main.contracts.actions.closeDeposit")}&contract=${this.contract.Data.ReferenceId}&sum=${this.contract.Data.Rest}`
          );
        }
      });
    }
    return actions;
  }

  /**Список информации */
  get details(): Array<any> {
    let details: Array<any> = [];
    if (this.contract) {
      details = [];
      details.push({
        //Остаток кредита
        name: i18n.t("main.contracts.details.restCredit"),
        textVal: `${this.contract.Rest} ${this.contract.Currency}`
      });
      details.push({
        //Остаток на связанном счете для погашения
        name: i18n.t("main.contracts.details.restCreditDepositSum"),
        textVal: `${this.contract.DepositSum} ${this.contract.Currency}`
      });
      details.push({
        //К погашению
        name: i18n.t("main.contracts.details.minPayDateText", {minPayDate: this.MinPayDate}),
        textVal: `${this.contract.MinPaySum} ${this.contract.Currency}`
      });

      if (this.contract.IsViDebtForm) {
        details.push({
          // Основной долг
          name: i18n.t("main.contracts.details.outstandingSumMain"),
          textVal: `${sumFilter(this.contract.Data.OutstandingSumMain)} ${this.contract.Currency}`
        });
        details.push({
          // Проценты 
          name: i18n.t("main.contracts.details.outstandingSumPrc"),
          textVal: `${sumFilter(this.contract.Data.OutstandingSumPrc)} ${this.contract.Currency}`
        });
        details.push({
          // Пени и штрафы
          name: i18n.t("main.contracts.details.penaltySum"),
          textVal: `${sumFilter(this.contract.Data.PenaltySum)} ${this.contract.Currency}`
        });
      }

      details.push({
        //К оплате
        name: i18n.t("main.contracts.details.creditToPay", {minPayDate: this.MinPayDate}),
        textVal: `${this.creditPaySum} ${this.contract.Currency}`
      });
      details.push({
        // Основной счёт
        name: i18n.t("main.contracts.details.mainAccount"),
        textVal: this.contract.Data.MainAccount
      });
      if (this.contract.Data.LinkedDepositAccountNum) {
        details.push({
          // Связанный счёт для погашения
          name: i18n.t("main.contracts.details.linkedDepositAccountNum"),
          textVal: this.contract.Data.LinkedDepositAccountNum
        });
      }
      details.push({
        //Валюта кредита
        name: i18n.t("main.contracts.details.currencyCredit"),
        textVal: this.contract.Currency
      });
      if (this.contract.Data.Number) {
        details.push({
          //Номер кредита
          name: i18n.t("main.contracts.details.creditNum"),
          textVal: this.contract.Data.Number
        });
      }
      details.push({
        //Тип кредита
        name: i18n.t("main.contracts.details.typeCredit"),
        textVal: this.contract.ContractType
      });
      if (this.contract.IsOpen) {
        details.push({
          // Ставка по кредиту
          name: i18n.t("main.contracts.details.creditPercentRate"),
          textVal: this.contract.Data.PercentRate ? `${this.contract.Data.PercentRate} %` : '-'
        });
      }
      details.push({
        //Состояние
        name: i18n.t("main.contracts.details.state"),
        textVal: this.contract.IsOpen
          ? i18n.t("main.contracts.details.open")
          : i18n.t("main.contracts.details.close")
      });
      if (this.contract.Data.DateOpen) {
        details.push({
          //Дата открытия
          name: i18n.t("main.contracts.details.dateOpen"),
          textVal: this.contract.DateOpen
        });
      }
      if (!this.contract.IsOpen) {
        //Дата закрытия
        details.push({
          name: i18n.t("main.contracts.details.dateClose"),
          textVal: this.contract.DateClose
        });
      }

      if(this.contract.IsOpen) {
        // Досрочное погашение
        details.push({
          name: i18n.t("main.contracts.details.canEarlyRepay"),
          textVal: this.contract.CanEarlyRepay
            ? i18n.t("main.contracts.details.enable")
            : i18n.t("main.contracts.details.disable")
        });
      }
    }
    return details;
  }
}